/**
 * CanonicalNpcsServiceModule — 2026-06-08 F3-FIX wire-up for the canonical
 * Core Rulebook Chapter 18 named NPCs (Kerim Bey / Goldfinger / Dr. No / etc.).
 *
 * Routes (per briefing_room_backend/routes/canonical_npcs.py):
 *   GET /api/modules/briefing-room/canonical-npcs                 → CanonicalNpcManifest
 *   GET /api/modules/briefing-room/canonical-npcs/{id}            → full entry | 404
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders().
 */
import { getAuthHeaders } from './authHeaders';

export type CanonicalNpcCategory = 'allies' | 'enemies' | 'see_chapter_1';

export interface CanonicalNpcManifestEntry {
  id: string;
  name: string;
  category: CanonicalNpcCategory;
  manifest_section: string;
  source_md_line: number | null;
}

export interface CanonicalNpcManifest {
  npcs: CanonicalNpcManifestEntry[];
  total: number;
  /**
   * How many additional Chapter 18 NPCs are enumerated as pending authoring.
   * The SPA surfaces this in a banner so the seed's incompleteness is
   * visible — key anti-enshittification lesson from this workstream.
   */
  pending_remaining_count: number;
  schema_version: string;
  source: string;
}

const BASE_PATH = '/api/modules/briefing-room/canonical-npcs';

export class CanonicalNpcsServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  async list(): Promise<CanonicalNpcManifest> {
    const resp = await this.fetchFn(BASE_PATH, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(
        `CanonicalNpcsServiceModule: list HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as CanonicalNpcManifest;
  }

  async get(npcId: string): Promise<Record<string, unknown> | null> {
    const resp = await this.fetchFn(
      `${BASE_PATH}/${encodeURIComponent(npcId)}`,
      {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      },
    );
    if (resp.status === 404) {
      return null;
    }
    if (!resp.ok) {
      throw new Error(
        `CanonicalNpcsServiceModule: get HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as Record<string, unknown>;
  }
}
